import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotifications } from '../contexts/NotificationContext';

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  
  const {
    notifications,
    stats,
    loading,
    markAsRead,
    deleteNotification,
    markAllAsRead,
    fetchNotifications,
    getNotificationsByContext,
    getUnreadByContext
  } = useNotifications();

  // یک‌بار در mount لیست را می‌گیریم.
  //
  // این قبلاً کامنت شده بود («temporarily disabled») و نتیجه‌اش این بود که
  // زنگولهٔ ادمین تا وقتی سوکت چیزی push نکند خالی می‌ماند — و سوکت هم به‌خاطر
  // ترنسپورت websocket اصلاً وصل نمی‌شد. یعنی عملاً هیچ اعلانی دیده نمی‌شد.
  useEffect(() => {
    if (fetchNotifications) {
      void fetchNotifications('admin_panel');
    }
  }, [fetchNotifications]);

  // Early return if notification system isn't ready
  if (!getNotificationsByContext || !getUnreadByContext) {
    return (
      <Box className={className}>
        <IconButton color="inherit">
          <Badge badgeContent={0} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Box>
    );
  }

  // Safe helper functions with comprehensive null checks
  const safeGetNotificationsByContext = (ctx: string) => {
    try {
      return getNotificationsByContext ? getNotificationsByContext(ctx) || [] : [];
    } catch (error) {
      console.warn('Error getting notifications by context:', error);
      return [];
    }
  };

  const safeGetUnreadByContext = (ctx: string) => {
    try {
      return getUnreadByContext ? getUnreadByContext(ctx) || 0 : 0;
    } catch (error) {
      console.warn('Error getting unread count by context:', error);
      return 0;
    }
  };

  // فقط نوتیفیکیشن‌های مربوط به admin_panel را نمایش می‌دهیم
  const adminNotifications = safeGetNotificationsByContext('admin_panel') || [];
  const unreadAdminNotifications = (adminNotifications || []).filter(n => n && !n.isRead);
  const displayNotifications = activeTab === 'all' ? (adminNotifications || []) : (unreadAdminNotifications || []);
  const unreadCount = safeGetUnreadByContext('admin_panel');

  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notificationId: string, isRead: boolean) => {
    if (!isRead && markAsRead) {
      try {
        await markAsRead(notificationId);
      } catch (error) {
        console.warn('Error marking notification as read:', error);
      }
    }
  };

  const handleDeleteNotification = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteNotification) {
      try {
        await deleteNotification(notificationId);
      } catch (error) {
        console.warn('Error deleting notification:', error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (markAllAsRead) {
      try {
        await markAllAsRead('admin_panel');
      } catch (error) {
        console.warn('Error marking all as read:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconMap: { [key: string]: string } = {
      // Admin specific notifications
      new_user_registration: '👤',
      new_seller_application: '🏪',
      seller_verification_request: '✅',
      new_product_submission: '📦',
      payment_dispute: '⚠️',
      system_alert: '🚨',
      security_breach: '🔒',
      server_maintenance: '🔧',
      backup_completed: '💾',
      user_report: '📋',
      financial_report: '💰',
      system_update: '🔄',
      database_error: '❌',
      api_limit_reached: '⚡',
      // Ticket notifications
      new_ticket: '🎫',
      ticket_assigned: '👨‍💼',
      ticket_status_changed: '🔄',
      ticket_message: '💬',
      ticket_transferred: '🔀',
      ticket_escalated: '🔺',
      // Access changes (role / temporary grant)
      role_updated: '🛡️',
      permission_granted: '🔑',
      permission_revoked: '🔒',
      // General notifications
      system: '⚙️',
      info: 'ℹ️',
      warning: '⚠️',
      error: '❌',
      success: '✅'
    };
    return iconMap[type] || '🔔';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'همین الان';
      if (diffInMinutes < 60) return `${diffInMinutes} دقیقه پیش`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} ساعت پیش`;
      
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'تاریخ نامعلوم';
    }
  };

  return (
    <Box className={className}>
      <IconButton
        onClick={handleClick}
        color="inherit"
        sx={{ 
          color: 'text.primary',
          '&:hover': { backgroundColor: 'action.hover' }
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            overflow: 'hidden'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              اعلان‌های ادمین
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Tabs */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant={activeTab === 'all' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('all')}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              همه ({adminNotifications.length})
            </Button>
            <Button
              size="small"
              variant={activeTab === 'unread' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('unread')}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              خوانده نشده ({unreadCount})
            </Button>
          </Box>

          {/* Mark all as read */}
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ mt: 1, fontSize: '0.75rem' }}
            >
              همه را خوانده شده علامت بزن
            </Button>
          )}
        </Box>

        {/* Notifications List */}
        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : displayNotifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                {activeTab === 'all' ? 'هیچ اعلانی وجود ندارد' : 'هیچ اعلان خوانده نشده‌ای وجود ندارد'}
              </Typography>
            </Box>
          ) : (
            displayNotifications.slice(0, 10).map((notification) => (
              <MenuItem
                key={notification._id}
                onClick={() => handleNotificationClick(notification._id, notification.isRead)}
                component="div"
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  backgroundColor: !notification.isRead ? 'action.hover' : 'transparent',
                  '&:hover': {
                    backgroundColor: !notification.isRead ? 'action.selected' : 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography component="span" sx={{ marginInlineEnd: 1, fontSize: '1.2rem' }}>
                        {getNotificationIcon(notification.type)}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight={!notification.isRead ? 'bold' : 'medium'}
                        sx={{ flex: 1 }}
                      >
                        {notification.title}
                      </Typography>
                      {!notification.isRead && (
                        <Chip
                          label="جدید"
                          size="small"
                          color="primary"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {notification.message}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(notification.createdAt)}
                      </Typography>
                      <Chip
                        label={notification.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Box>
                  </Box>
                  
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteNotification(notification._id, e)}
                    sx={{ marginInlineStart: 1, color: 'text.secondary' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>

        {/* Footer */}
        {displayNotifications.length > 0 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                handleClose();
                // Navigate to notifications page if needed
              }}
              sx={{ textTransform: 'none' }}
            >
              مشاهده همه اعلان‌ها
            </Button>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default NotificationBell; 